import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useMessages } from '../contexts/MessageContext';
import Sidebar from '../components/Sidebar';
import config from '../config';
import { messageService } from '../utils/messageService';

export default function MessagesPage() {
  const { user } = useAuth();
  const {
    messages,
    typingUsers,
    onlineUsers,
    sendMessage,
    sendTypingIndicator,
    markAsRead,
    setMessages,
    isConnected
  } = useMessages();
  
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [connectionState, setConnectionState] = useState(messageService.getConnectionState());
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Monitor connection state
  useEffect(() => {
    const handleConnectionState = (state) => {
      setConnectionState(state);
    };

    messageService.on('connectionStateChanged', handleConnectionState);
    return () => messageService.off('connectionStateChanged', handleConnectionState);
  }, []);

  // Memoize filtered messages
  const filteredMessages = useMemo(() => {
    return selectedContact
      ? messages
          .filter(m => {
            const userIdStr = user.sub?.toString();
            const contactIdStr = selectedContact.userId?.toString();
            
            const isUserSender = m.senderId === userIdStr && m.recipientId === contactIdStr;
            const isUserRecipient = m.senderId === contactIdStr && m.recipientId === userIdStr;
            
            return isUserSender || isUserRecipient;
          })
          .sort((a, b) => {
            // Sort by UTC timestamp (oldest first for display)
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            return timeA - timeB;
          })
      : [];
  }, [messages, selectedContact, user.sub]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when messages change or when selected contact changes
  useEffect(() => {
    scrollToBottom();
  }, [filteredMessages, selectedContact]);

  // Scroll to bottom when component mounts
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Fetch contacts (clients for midwives, midwife for clients)
  useEffect(() => {
    const fetchContacts = async () => {
      if (!user?.sub) {
        console.log('No user ID available yet, waiting...');
        return;
      }

      try {
        const token = localStorage.getItem('velaToken');
        const userRole = localStorage.getItem('userRole');
        
        console.log('Starting fetchContacts...');
        console.log('User role from localStorage:', userRole);
        console.log('User object:', user);

        if (!userRole) {
          console.error('No user role found in localStorage');
          return;
        }

        let endpoint;
        if (userRole === 'midwife') {
          endpoint = `${config.api.url}/midwives/${user.sub}/clients`;
        } else if (userRole === 'client') {
          endpoint = `${config.api.url}/Clients/${user.sub}/midwife`;
        } else {
          console.error('Unsupported user role:', userRole);
          return;
        }
        
        console.log('Fetching contacts from endpoint:', endpoint);

        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch contacts:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Contact data received:', data);
        
        if (userRole === 'client') {
          if (!data || (!data.midwifeId && !data.userId)) {
            console.error('Invalid midwife data received:', data);
            return;
          }
          
          // If client, we need to fetch the midwife's user details
          const midwifeId = data.userId;
          console.log('Fetching midwife details for ID:', midwifeId);
          
          if (!midwifeId) {
            console.error('No midwife ID found in data:', data);
            return;
          }
          
          const midwifeResponse = await fetch(`${config.api.url}/User/${midwifeId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (!midwifeResponse.ok) {
            console.error('Failed to fetch midwife details:', midwifeResponse.status, midwifeResponse.statusText);
            const errorText = await midwifeResponse.text();
            console.error('Error response:', errorText);
            throw new Error('Failed to fetch midwife details');
          }
          
          const midwifeData = await midwifeResponse.json();
          console.log('Midwife user data:', midwifeData);
          
          // Create midwife contact
          const midwifeContact = {
            userId: midwifeId,
            firstName: midwifeData.firstName || 'Loading',
            lastName: midwifeData.lastName || '...',
            email: midwifeData.email
          };
          
          if (!midwifeContact.firstName || !midwifeContact.lastName) {
            console.warn('Midwife name data incomplete:', midwifeData);
            setIsLoading(false);
            return;
          }

          console.log('Created midwife contact:', midwifeContact);
          setContacts([midwifeContact]);
          setSelectedContact(midwifeContact); // Auto-select for clients since they only have one midwife
          setIsLoading(false);
        } else {
          // For midwives, the data is already in the correct format
          console.log('Setting contacts for midwife:', data);
          setContacts(data.map(contact => ({
            ...contact,
            firstName: contact.firstName || 'Loading',
            lastName: contact.lastName || '...'
          })));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error in fetchContacts:', err);
        setIsLoading(false);
      }
    };

    if (user) {
      console.log('Fetching contacts for user:', user);
      fetchContacts();
    }
  }, [user]);

  // Mark messages as read when selecting a contact
  useEffect(() => {
    if (selectedContact) {
      const unreadMessages = messages.filter(
        m => m.senderId === selectedContact.id && !m.read
      );
      if (unreadMessages.length > 0) {
        markAsRead(unreadMessages.map(m => m.id));
      }
    }
  }, [selectedContact, messages, markAsRead]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedContact) return;
    if (!isConnected) {
      console.error('Cannot send message: Not connected');
      // You might want to show a toast notification here
      return;
    }

    try {
      const success = await messageService.sendMessage(selectedContact.userId, messageInput.trim());
      if (success) {
        // Clear input - the message will be added to the list via the messageReceived handler
        setMessageInput('');
      } else {
        console.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    
    // Handle typing indicator
    if (selectedContact?.userId && isConnected) {
      clearTimeout(typingTimeoutRef.current);
      sendTypingIndicator(selectedContact.userId);
      typingTimeoutRef.current = setTimeout(() => {
        typingTimeoutRef.current = null;
      }, 3000);
    }
  };

  // Function to format timestamp to local time
  const formatLocalTime = (utcTimestamp) => {
    try {
      // Ensure we're working with a UTC timestamp
      const utcDate = new Date(utcTimestamp);
      if (isNaN(utcDate.getTime())) {
        throw new Error('Invalid timestamp');
      }
      
      // Get local time components
      const localHours = utcDate.getHours();
      const localMinutes = utcDate.getMinutes();
      const ampm = localHours >= 12 ? 'PM' : 'AM';
      const displayHours = localHours % 12 || 12;
      const displayMinutes = localMinutes.toString().padStart(2, '0');
      
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch (err) {
      console.error('Error formatting timestamp:', err, utcTimestamp);
      return 'Invalid time';
    }
  };

  // Remove debug logging effects
  useEffect(() => {}, [messages, filteredMessages]);

  const getConnectionStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-400';
      case 'reconnecting':
        return 'bg-yellow-400';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionState) {
      case 'connected':
        return 'Connected';
      case 'reconnecting':
        return 'Reconnecting...';
      case 'error':
        return 'Connection Error';
      default:
        return 'Disconnected';
    }
  };

  return (
    <Sidebar>
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* Contacts sidebar */}
        <div className="w-80 border-r bg-white overflow-y-auto">
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-700">Messages</h2>
            <div className="flex items-center mt-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${getConnectionStatusColor()} mr-2`} />
              <span className="text-gray-600">{getConnectionStatusText()}</span>
            </div>
          </div>
          <div className="divide-y">
            {contacts.map(contact => (
              <button
                key={contact.userId || contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`w-full p-4 text-left hover:bg-gray-50 flex items-center space-x-3 ${
                  selectedContact?.userId === contact.userId ? 'bg-emerald-50' : ''
                }`}
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-800 font-medium">
                      {(contact.firstName?.[0] || '').toUpperCase()}
                      {(contact.lastName?.[0] || '').toUpperCase()}
                    </span>
                  </div>
                  {onlineUsers.has(contact.userId) && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {contact.firstName && contact.lastName ? 
                      `${contact.firstName} ${contact.lastName}` : 
                      'Loading...'}
                  </p>
                  {typingUsers.has(contact.userId) && (
                    <p className="text-sm text-emerald-600">Typing...</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedContact ? (
            <>
              {/* Chat header */}
              <div className="p-4 border-b bg-white">
                <h3 className="text-lg font-semibold">
                  {selectedContact?.firstName && selectedContact?.lastName ? 
                    `${selectedContact.firstName} ${selectedContact.lastName}` :
                    'Loading contact...'
                  }
                </h3>
                {selectedContact?.email && (
                  <p className="text-sm text-gray-500">{selectedContact.email}</p>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {filteredMessages.map((message, index) => (
                  <div
                    key={message.id || `${message.timestamp}-${index}`}
                    className={`flex ${
                      message.senderId === user.sub ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-2 ${
                        message.senderId === user.sub
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white text-gray-900'
                      }`}
                    >
                      <p>{message.content}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {formatLocalTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t bg-white">
                <div className="flex space-x-4">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={handleInputChange}
                    placeholder={selectedContact ? "Type a message..." : "Select a contact to start messaging"}
                    disabled={!selectedContact || !isConnected}
                    className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim() || !selectedContact || !isConnected}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Select a contact to start messaging
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
} 